import React, {useMemo, useState} from "react";
import {arrayUpdatePartial, ArrayUpdateType} from "../../immutableState";
import {classNames} from "../../wrapper";
import Input from "../../components/Input";
import TestCodes from "../../TestCodes";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import {useValidationArray} from "../validationArray";
import {FarmerRow, emptyFarmerRow} from "./SubmissionPrepareFarmer";
import GenericSelect from "../../components/GenericSelect";
import {pasteInColumn} from "../pasteColumn";
import TableCore, {CountDisplay, TableColumn} from "../../components/TableCore";

const SubmissionTable: React.FC<{
    leafs: (FarmerRow)[]
    data: SubmissionDataResponse<FarmerRow>
    setData: (data: (FarmerRow)[]) => void
    index: number
    registerValidation?: (index: number, validate: () => boolean) => void;
}> = (props) => {
    
    const [headerTest, setHeaderTest] = useState<number | null>(null)
    const [headerOptionalTests, setHeaderOptionalTests] = useState<number[]>([]);
    const [split , setSplit] = useState<boolean>(false)

    function lengthCheck(length: number, deleted: boolean) {
        // if it's deleted, it's fine and the validation should pass
        return length > 0 || deleted;
    }

    function duplicatesampleNumberCheck(sampleNumber: string) {
        return props.leafs.filter(l => l.sampleNumber === sampleNumber).length === 1;
    }

    const validation = useValidationArray(props.leafs)({
        sampleNumber: leaf => lengthCheck(leaf.sampleNumber.length, leaf.delete) && duplicatesampleNumberCheck(leaf.sampleNumber),
        block: leaf => lengthCheck(leaf.blockNumber.length, leaf.delete),
        test: leaf => leaf.test != TestCodes.Unknown
            && leaf.test != TestCodes.ItestFarmerSample,
    })

    props.registerValidation?.(props.index, () => validation.validate());
    
    function setDataIndex(index: number, update: ArrayUpdateType<FarmerRow>) {
        props.setData(arrayUpdatePartial( props.leafs, index, update))
    }
    
    function setLeafData(update: (leaf: FarmerRow) => FarmerRow) {
        const leafs = props.leafs;
        props.setData(leafs.map((l, i) => {
            return update(l)
        }))
    }
    function updateHeaderTest(test: number) {
        setHeaderTest(test);

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, test: test};
        })
    }

    function updateHeaderOptionalTest(optionalTests: number[]) {
        setHeaderOptionalTests(optionalTests);

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, optionalTests: [...optionalTests]};
        })
    }

    const allSelectedDeleted = props.leafs.some(l => l.selected) && props.leafs.filter(l => l.selected).every(l => l.delete);

    function toggleHeaderDelete() {
        setLeafData(l => l.selected ? {...l, delete: !allSelectedDeleted} : l);
    }

    // Selection: sync with row's selected property
    const selectedKeys = props.leafs
        .map((l, i) => ({selected: l.selected, index: i}))
        .filter(l => l.selected)
        .map(l => l.index);

    function handleSelectionChange(keys: number[]) {
        props.setData(props.leafs.map((l, i) => ({...l, selected: keys.includes(i)})));
        if (keys.length === 0) setSplit(false);
    }

    const columns: TableColumn<FarmerRow>[] = useMemo(() => [
        {
            header: () => <div className="p-2 text-xs align-bottom">Sample Number</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <Input
                            onPaste={e => pasteInColumn(e, index, v => ({sampleNumber: v}), props.leafs, props.setData, emptyFarmerRow)}
                            className={leafValidation?.rules.sampleNumber ? '' : 'border-1 border-red-600'}
                            value={item.sampleNumber}
                            change={v => setDataIndex(index, {sampleNumber: v})}/>
                        {item.notes}
                    </div>
                );
            }
        },
        {
            header: () => <div className="p-2 text-xs align-bottom">Block</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <Input
                            onPaste={e => pasteInColumn(e, index, v => ({blockNumber: v}), props.leafs, props.setData, emptyFarmerRow)}
                            className={leafValidation?.rules.block ? '' : 'border-1 border-red-600'}
                            value={item.blockNumber}
                            change={v => setDataIndex(index, {blockNumber: v})}/>
                    </div>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Tests</div>
                    <GenericSelect
                        options={props.data.tests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={headerTest}
                        onChange={v => updateHeaderTest(Number(v))}/>
                </div>
            ),
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <GenericSelect
                        className={leafValidation?.rules.test ? '' : 'border-1 border-red-600'}
                        options={props.data.tests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={item.test}
                        onChange={v => setDataIndex(index, {test: Number(v)})}/>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Optional Tests</div>
                    <GenericSelect
                        options={props.data.optionalTests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={headerOptionalTests || []}
                        multiple={true}
                        onChange={v => updateHeaderOptionalTest(((v as string[]) ?? []).map(Number))}/>
                </div>
            ),
            row: (item, index) => (
                <GenericSelect
                    options={props.data.optionalTests}
                    textFunc={c => c.value}
                    valueFunc={c => c.key}
                    value={item.optionalTests || []}
                    multiple={true}
                    onChange={v => setDataIndex(index, {optionalTests: ((v as string[]) ?? []).map(Number)})}/>
            )
        },
        {
            header: () => <div className="text-xs align-bottom text-center">
                <div>Actions</div>
                <div className={allSelectedDeleted ? "btn-sm btn-primary" : "btn-sm btn-error"} onClick={toggleHeaderDelete}>
                    {allSelectedDeleted ? '+' : '-'}
                </div>
            </div>,
            row: (item, index) => (
                <div className="table-data">
                    {item.delete
                        ? <div className="btn-sm btn-primary" onClick={() => setDataIndex(index, {delete: false})}>+</div>
                        : <div className="btn-sm btn-error" onClick={() => setDataIndex(index, {delete: true})}>-</div>
                    }
                </div>
            )
        }
    ], [props.data, headerTest, headerOptionalTests, validation.items]);

    return (
        <TableCore
            data={props.leafs}
            columns={columns}
            keyExtractor={(_, index) => index}
            headerClass="bg-gray-100"
            selection={{
                selectedKeys: selectedKeys,
                onSelectionChange: handleSelectionChange,
                selectAll: true,
                countDisplay: CountDisplay.CountOfTotal
            }}
        />
    );
}

export default SubmissionTable;
